import { useCallback, useEffect, useState } from 'react';
import { eventsAPI } from '../../../api/events';

// Stored nightly events of the given types as chart markers, in the library's event shape
// ({ id, t, payload }). Daily rows over the nightly lookback (~3 years), paged.
const DAYS_BACK = 1100;
const MAX_PAGES = 10;

/**
 * `toLibraryEvent` maps one /v2/events row, or returns null to drop it. `refetch` reloads in
 * place (the page's poll calls it so a parked tab picks up the nightly run); a symbol change
 * starts empty.
 */
export default function useStoredEventMarkers(symbol, { eventTypes, toLibraryEvent, label }) {
  const [events, setEvents] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const refetch = useCallback(() => setRefreshKey((key) => key + 1), []);
  const typesKey = eventTypes.join(',');

  useEffect(() => {
    setEvents([]);
  }, [symbol]);

  useEffect(() => {
    let cancelled = false;
    if (!symbol) {return undefined;}
    (async () => {
      try {
        const rows = [];
        let cursor;
        for (let page = 0; page < MAX_PAGES; page += 1) {
          // eslint-disable-next-line no-await-in-loop
          const response = await eventsAPI.getRecentEvents({
            scope: 'system',
            symbol,
            timeframe: '1d',
            eventTypes: typesKey.split(','),
            days: DAYS_BACK,
            limit: 200,
            sortOrder: 'desc',
            cursor,
          });
          rows.push(...(response?.events || []));
          cursor = response?.cursor || response?.nextToken;
          if (!cursor) {break;}
        }
        if (!cancelled) {setEvents(rows.map(toLibraryEvent).filter(Boolean));}
      } catch (error) {
        if (!cancelled) {console.error(`useStoredEventMarkers: failed to load ${label} events`, error);}
      }
    })();
    return () => { cancelled = true; };
    // toLibraryEvent and label are module-level constants at every call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, refreshKey, typesKey]);

  return { events, refetch };
}
